import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { revokeImpersonation } from '../../api/admin';
import { logout } from '../../api/auth';
import { useMe } from '../auth/useMe';
import { ErrorNotice } from '../../components/ErrorNotice';

/**
 * Impersonation banner (ARCHITECTURE_PLAN §6.4). Rendered at the top of the
 * workspace shell when the session is an impersonation session. Names the
 * tenant the admin is impersonating into and offers:
 *   - "End impersonation" — revokes the grant on the server, then clears all
 *     cached server state and re-bootstraps `GET /api/auth/me`. The server
 *     destroys the impersonation session's cookie (logout) and the admin's
 *     original session cookie is gone (it was replaced when impersonation
 *     started). The admin is routed back to /login to re-authenticate.
 *   - a visible reminder that billing is denied under impersonation.
 *
 * The banner is intentionally impossible to dismiss — §6.4 requires a
 * "visible impersonation banner" for the duration of the impersonation.
 */
export function ImpersonationBanner(): React.JSX.Element | null {
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const endMutation = useMutation({
    mutationFn: async (grantId: string) => {
      await revokeImpersonation(grantId);
      // The impersonation session cookie was replaced when impersonation
      // started; revoking the grant makes the next request under that
      // session 403 (the workspace middleware checks grant validity on
      // every request). Destroy the session server-side so no lingering
      // 403 audit rows are produced, then re-bootstrap.
      await logout();
    },
    onSuccess: () => {
      queryClient.clear();
      void navigate('/login', { replace: true });
    },
  });

  if (!me.data?.session.isImpersonating) {
    return null;
  }
  const grantId = me.data.session.impersonationGrantId;
  const slug = me.data.session.impersonatingTenantSlug;

  return (
    <div
      role="alert"
      data-testid="impersonation-banner"
      className="border-b border-amber-300 bg-amber-100 px-6 py-2 text-sm text-amber-900"
    >
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-4">
        <div>
          <span className="font-semibold">Impersonating</span>{' '}
          <code className="font-mono text-xs">{slug ?? 'unknown tenant'}</code>
          <span className="ml-2 text-amber-800">
            You have admin capabilities MINUS billing (§6.4). Every action is audited.
          </span>
        </div>
        <button
          type="button"
          onClick={() => {
            if (grantId !== null) {
              endMutation.mutate(grantId);
            }
          }}
          disabled={endMutation.isPending || grantId === null}
          className="rounded-md border border-amber-700 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="end-impersonation-btn"
        >
          {endMutation.isPending ? 'Ending…' : 'End impersonation'}
        </button>
      </div>
      {endMutation.isError && (
        <div className="mx-auto mt-2 max-w-5xl">
          <ErrorNotice error={endMutation.error} />
        </div>
      )}
    </div>
  );
}
